import React from 'react'
import { useSelector } from 'react-redux'
import { t } from 'ttag'

import { updatePath } from 'utils/object'

import { getLanguage } from 'ducks/auth'

import {
  CATEGORY_PLACEMENT,
  INSERTED_CATEGORY,
  MODES,
  STATIC_PAGES,
  getNewCategory,
} from '../model'

// NOTE: hook that handles state and logic to maintain
// steps transition, switching mode from read only to edit,
// adding new category, adding questions etc. and exposes
// state and handlers for other local hooks and the
// interview template creator components
export const useStepsState = (state, handlers) => {
  const { sections, skills } = state
  const { handleSetResponse } = handlers
  const uiLanguage = useSelector(getLanguage)

  const [selectedStep, setActiveStep] = React.useState(null)
  // the first step until one is chosen, so the first render after the fetch
  // already has an active section
  const activeStep = selectedStep ?? sections[0]?.key ?? null
  const [baseStep, setBaseStep] = React.useState(null)
  const [mode, setMode] = React.useState(MODES.IDLE)

  const scoredSectionsCount = sections.filter(
    ({ scoreable }) => scoreable,
  ).length
  const sectionsCount = sections.filter(
    ({ key }) => key !== INSERTED_CATEGORY,
  ).length
  const questionsCount = sections
    .filter(({ key }) => !STATIC_PAGES.includes(key))
    .reduce((acc, section) => acc + section.questions?.length, 0)

  const competenciesCount = Object.values(skills).filter(val => val).length

  const isEditMode = mode === MODES.EDIT
  const isIdleMode = mode === MODES.IDLE

  const activeSection = React.useMemo(
    () =>
      sections.find(section => section?.key === activeStep && section) ?? {},
    [activeStep, sections],
  )

  const steps = React.useMemo(
    () =>
      sections.map(({ key, scoreable, title }) => {
        const isActive = activeStep === key
        const isDisabled = isActive ? false : isEditMode ? true : false
        const isEdited = isEditMode

        return {
          key,
          label: title || t`New category`,
          scoreable,
          isActive,
          isDisabled,
          isEdited,
        }
      }),
    [activeStep, sections, isEditMode, uiLanguage],
  )

  const currentStepIndex = React.useMemo(
    () => steps.findIndex(({ key }) => key === activeStep),
    [activeStep, steps],
  )

  const isNextStepDisabled = activeStep === steps[steps.length - 1]?.key
  const isPreviousStepDisabled = activeStep === steps[0]?.key
  const isStaticPage = STATIC_PAGES.includes(activeStep)

  const hasNoScoreableCategory = scoredSectionsCount <= 1
  const isActiveCategoryScorable = activeSection?.scoreable === true
  const isInsertedCategory = activeSection?.key === INSERTED_CATEGORY
  const isCategoryScorable = !(
    hasNoScoreableCategory && isActiveCategoryScorable
  )
  const isCategoryDeletable = isCategoryScorable && !isInsertedCategory
  const isNextCategoryDisabled = isNextStepDisabled && isStaticPage
  const isPreviousCategoryDisabled = isPreviousStepDisabled && isStaticPage

  const handleChangeStep = React.useCallback(
    step => {
      setActiveStep(step)
    },
    [setActiveStep],
  )

  const handleChangeBaseStep = React.useCallback(
    step => {
      setBaseStep(step)
    },
    [setBaseStep],
  )

  const handleChangeNextStep = React.useCallback(() => {
    if (currentStepIndex >= steps.length - 1) return

    const nextStep = steps[currentStepIndex + 1]?.key

    handleChangeStep(nextStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangePreviousStep = React.useCallback(() => {
    if (currentStepIndex <= 0) return

    const previousStep = steps[currentStepIndex - 1]?.key

    handleChangeStep(previousStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangeMode = React.useCallback(() => {
    setMode(prevState => (prevState === MODES.IDLE ? MODES.EDIT : MODES.IDLE))
  }, [setMode])

  const handleInsertCategory = React.useCallback(
    ({ type }) =>
      () => {
        const index =
          type === CATEGORY_PLACEMENT.NEXT
            ? currentStepIndex + 1
            : currentStepIndex

        handleSetResponse(prevState => {
          handleChangeStep(INSERTED_CATEGORY)
          handleChangeBaseStep(activeStep)

          return updatePath(prevState, ['sections'], sections => {
            const nextSections = [...(sections as unknown[])]
            nextSections.splice(index, 0, getNewCategory())
            return nextSections
          })
        })
        handleChangeMode()
      },
    [
      activeStep,
      currentStepIndex,
      handleChangeBaseStep,
      handleChangeMode,
      handleChangeStep,
      handleSetResponse,
    ],
  )

  const handleInsertCategoryCancel = React.useCallback(() => {
    handleSetResponse(prevState => {
      handleChangeStep(baseStep)
      handleChangeBaseStep(null)

      return updatePath(prevState, ['sections'], sections => {
        const nextSections = [...(sections as unknown[])]
        nextSections.splice(currentStepIndex, 1)
        return nextSections
      })
    })
    handleChangeMode()
  }, [
    baseStep,
    currentStepIndex,
    handleChangeBaseStep,
    handleChangeMode,
    handleChangeStep,
    handleSetResponse,
  ])

  return {
    state: {
      activeSection,
      activeStep,
      currentStepIndex,
      questionsCount,
      sectionsCount,
      competenciesCount,
      steps,
      isCategoryScorable,
      isCategoryDeletable,
      isEditMode,
      isIdleMode,
      isInsertedCategory,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeMode,
      handleChangeStep,
      handleInsertCategory,
      handleInsertCategoryCancel,
    },
  }
}
